/**
 * Listing Domain Types
 *
 * Unicorn Standard: Domain types are shared across the entire app.
 * View contracts (component props) import from here, not vice versa.
 *
 * Pattern: Stringified proto enums for type-safe switch statements.
 * - Cards, Detail pages, Filters, URL params all use these types
 * - Compile-time error if you typo 'IN_PROGRES' in a switch case
 *
 * Two patterns for enum types:
 * 1. `keyof typeof Enum` - When proto enum names are used directly (IN_PROGRESS, LISTED)
 * 2. Lowercase literal union - When user-facing/URL values differ ('rent', 'sale')
 */

// Value import: the dwelling-type SLUG vocabulary is generated, not typed here.
import { PROTO_ENUM_SLUG_SET_PropertyType } from '@letsgethome/lib/proto-enums/listing/value_sets_gen';
import {
  type ListingPublicationState,
  type WizardStep,
} from '@letsgethome/proto/account/v1/listings_pb';
import type { PaymentStatus } from '@letsgethome/proto/billing/v1/payment_status_pb';
import { type ProcessingState } from '@letsgethome/proto/common/v1/processing_state_pb';
import { type ImageRejectionReason } from '@letsgethome/proto/imagery/v1/image_rejection_reason_pb';
import { type ImageScreening } from '@letsgethome/proto/imagery/v1/image_screening_pb';
import { type AmenityCategory } from '@letsgethome/proto/listing/v1/amenity_category_pb';
import { type FactOrigin } from '@letsgethome/proto/listing/v1/fact_origin_pb';
import { type FloorLevel } from '@letsgethome/proto/listing/v1/floor_level_pb';
import { type ListingReportReason } from '@letsgethome/proto/listing/v1/listing_report_reason_pb';
import { type ListingReportStatus } from '@letsgethome/proto/listing/v1/listing_report_status_pb';
import { type ListingStatus } from '@letsgethome/proto/listing/v1/listing_status_pb';
import { type ListingSuspensionReason } from '@letsgethome/proto/listing/v1/listing_suspension_reason_pb';
import { type PetsPolicy } from '@letsgethome/proto/listing/v1/pets_policy_pb';
// Value import, not type-only: `asPropertyTypeName` derives its vocabulary
// from the generated enum at runtime.
import { PropertyType } from '@letsgethome/proto/listing/v1/property_type_pb';

// ----------------------------------------------------------------------------
// Stringified Proto Enum Types (keyof typeof pattern)
// ----------------------------------------------------------------------------

/**
 * Stringified listing status for type-safe UI code.
 * Example values: 'UNSPECIFIED' | 'IN_PROGRESS' | 'LISTED' | 'UNLISTED' | 'DELETED'
 */
export type ListingStatusName = keyof typeof ListingStatus;

/**
 * Stringified async-pipeline phase for type-safe UI code.
 * Example values: 'UNSPECIFIED' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
 *
 * A listing's `translation_state` — the server's aggregate over its
 * `listing_translation_jobs` rows — is the one this app reads: it is the
 * second input to the publish-hold decision (`@/shared/lib/listings/status`).
 */
export type ProcessingStateName = keyof typeof ProcessingState;

/**
 * D3 — WHY staff suspended a listing, as a variant NAME. The owner's page
 * renders the hold with the reason and a remediation keyed on it; `UNSPECIFIED`
 * never reaches a view model (it is the wire's "not suspended").
 */
export type ListingSuspensionReasonName = Exclude<
  keyof typeof ListingSuspensionReason,
  'UNSPECIFIED'
>;

/** D1 — a photo's admission verdict by name (UNSPECIFIED never reaches a view model). */
export type ImageScreeningName = Exclude<keyof typeof ImageScreening, 'UNSPECIFIED'>;

/** D1 — why a photo was refused, by name. */
export type ImageRejectionReasonName = Exclude<keyof typeof ImageRejectionReason, 'UNSPECIFIED'>;

/**
 * Where a listing stands on the public storefront, from its OWNER's chair, as a
 * variant NAME — `account.v1.ListingPublicationState`, derived server-side by
 * `domain_listing_lifecycle::listing_publication`. `status` is the owner's
 * intent; this is the derived fact (a LISTED listing below the photo floor is
 * `HIDDEN_BELOW_PHOTO_FLOOR`). `UNSPECIFIED` never reaches a view model.
 */
export type ListingPublicationStateName = Exclude<
  keyof typeof ListingPublicationState,
  'UNSPECIFIED'
>;

/**
 * Stringified floor level for type-safe UI code.
 * Example values: 'UNSPECIFIED' | 'LOWER' | 'MIDDLE' | 'HIGHER' | 'PENTHOUSE'
 */
export type FloorLevelName = keyof typeof FloorLevel;

/**
 * Stringified dwelling form for type-safe UI code (SEO F11).
 * Example values: 'UNSPECIFIED' | 'ESTATE_FLAT' | 'VILLAGE_HOUSE' | …
 */
export type PropertyTypeName = keyof typeof PropertyType;

/**
 * Stringified amenity category (LET-99) — the display grouping of one amenity.
 * Values: 'UNSPECIFIED' | 'FACILITY' | 'FEATURE' | 'APPLIANCE' | 'SERVICE'.
 */
export type AmenityCategoryName = keyof typeof AmenityCategory;

/**
 * D11 — who asserted a listing room count, stated (never UNSPECIFIED: the
 * origin is absent exactly when the count is).
 * Values: 'OWNER' | 'STAFF' | 'INFERRED'.
 */
export type FactOriginName = Exclude<keyof typeof FactOrigin, 'UNSPECIFIED'>;

/**
 * Stringified pet policy (LET-91) — what the OWNER SAID about pets.
 * Values: 'UNSPECIFIED' | 'ALLOWED' | 'NOT_ALLOWED'.
 */
export type PetsPolicyName = keyof typeof PetsPolicy;

/**
 * D4 — a reason a customer can REPORT a listing for, as the report dialog
 * picks it. `UNSPECIFIED` is not a reason (the server refuses it), so the
 * dialog's value space excludes it by type.
 */
export type StatedListingReportReasonName = Exclude<
  keyof typeof ListingReportReason,
  'UNSPECIFIED'
>;

/**
 * #8214 — where a report stands, as its REPORTER reads it on `/account/reports`.
 * `UNSPECIFIED` is not storable (`listing_reports_status_not_unspecified_chk`),
 * so the mapper refuses it and the page's status map is total over the rest.
 */
export type StatedListingReportStatusName = Exclude<
  keyof typeof ListingReportStatus,
  'UNSPECIFIED'
>;

/**
 * A pet policy the owner actually STATED. `UNSPECIFIED` ("the owner did not
 * say") is excluded by type, so a view model carries `undefined` for it and a
 * component cannot render a chip for a policy nobody asserted — the shape
 * that made "Pets: Allowed" from a DB default unrepresentable (LET-93).
 */
export type StatedPetsPolicy = Exclude<PetsPolicyName, 'UNSPECIFIED'>;

/**
 * The dwelling-form NAME vocabulary, derived from the generated enum — the
 * one place an untrusted string becomes a `PropertyTypeName`.
 *
 * Needed because the listings filter codec carries the name as a bare string
 * (`packages/routes` sits below `packages/proto` in the web DAG and must not
 * import generated types), so the URL's value has to be narrowed somewhere on
 * the app side. Deriving the set from the enum means a renamed or removed
 * variant narrows to `undefined` instead of reaching the wire.
 */
const PROPERTY_TYPE_NAMES: ReadonlySet<string> = new Set(
  Object.entries(PropertyType)
    .filter(([, value]) => typeof value === 'number')
    .map(([name]) => name)
);

/**
 * Narrow an untrusted string to a `PropertyTypeName`. An unrecognised name — a
 * stale link, a typo, a probe — is `undefined`, i.e. "no dwelling-form
 * filter", never a request the wire would reject.
 */
export function asPropertyTypeName(raw: string | undefined): PropertyTypeName | undefined {
  if (raw === undefined || !PROPERTY_TYPE_NAMES.has(raw)) return undefined;
  const name = raw as PropertyTypeName;
  // UNSPECIFIED is the enum's own absent state, so it is not a filter either.
  return name === 'UNSPECIFIED' ? undefined : name;
}

/**
 * A dwelling-type URL SLUG — the `village-house` in
 * `/listings/rent/village-house` (SEO G4).
 *
 * A distinct type from `PropertyTypeName` on purpose: the NAME is the wire
 * vocabulary (`VILLAGE_HOUSE`, what a `?property_type=` param carries), the
 * SLUG is the URL vocabulary. They differ in case and separator, and the one
 * place they are allowed to meet is the generated table below.
 */
export type PropertyTypeSlug = (typeof PROPERTY_TYPE_SLUGS)[number];

/**
 * The dwelling-type slug vocabulary — GENERATED by protomirrorgen from
 * `listing.v1.PropertyType`, the same source as the Rust
 * `ValidPropertyType::slug()` the backend narrows with and the sitemap files
 * URLs from. Nothing here is hand-written, so a new variant mints its slug on
 * all three surfaces at once and a stale one cannot linger on any of them.
 */
const PROPERTY_TYPE_SLUGS = PROTO_ENUM_SLUG_SET_PropertyType;
const PROPERTY_TYPE_SLUG_SET: ReadonlySet<string> = new Set(PROPERTY_TYPE_SLUGS);

/**
 * Narrow an untrusted URL segment to a `PropertyTypeSlug`.
 *
 * THE 404 seam for the type-facet routes, and the exact analogue of
 * `isDistrictSlug`: a segment outside the generated vocabulary is not a page,
 * so the loader 404s rather than serving an empty grid under a made-up
 * heading.
 */
export function isPropertyTypeSlug(raw: string): raw is PropertyTypeSlug {
  return PROPERTY_TYPE_SLUG_SET.has(raw);
}

/**
 * Stringified wizard step for type-safe UI code.
 * Example values: 'UNSPECIFIED' | 'ADDRESS' | 'SIZE' | 'PRICING' | 'MEDIA' | 'DESCRIPTION'
 */
export type WizardStepName = keyof typeof WizardStep;

/** billing.v1.PaymentStatus NAME union (ADR 0151). */
export type PaymentStatusName = keyof typeof PaymentStatus;
